import type { AIMessage, Message, ToolMessage } from "@langchain/langgraph-sdk";
import type {
  DisplayContentItem,
  SubagentDisplayInfo,
  TextContentItem,
  ToolCallResultItem,
  ToolCallStartItem,
} from "./AiToolboxSection.types";

export const TASK_TOOL_NAME = "task";

export const isForbiddenError = (error: unknown): boolean =>
  error instanceof Error && (error as { status?: number }).status === 403;

export const isTextItem = (item: DisplayContentItem): item is TextContentItem =>
  item.kind === "text";

export const isToolCallStartItem = (
  item: DisplayContentItem
): item is ToolCallStartItem => item.kind === "tool_call_start";

export const isToolCallResultItem = (
  item: DisplayContentItem
): item is ToolCallResultItem => item.kind === "tool_call_result";

export function messageContentToString(content: Message["content"]): string {
  if (typeof content === "string") return content;
  return content
    .filter((c): c is { type: "text"; text: string } => c.type === "text")
    .map((c) => c.text)
    .join("");
}

function isToolErrorBody(body: string): boolean {
  try {
    const parsed = JSON.parse(body);
    return Boolean(parsed && typeof parsed === "object" && parsed.__tool_error__);
  } catch {
    return false;
  }
}

export function messagesToDisplayContent(messages: Message[]): DisplayContentItem[] {
  const items: DisplayContentItem[] = [];

  for (const msg of messages) {
    if (msg.type === "ai" || (msg.type as string) === "AIMessageChunk") {
      const textContent = messageContentToString(msg.content);

      if (textContent) {
        const last = items[items.length - 1];
        if (last && isTextItem(last) && last.id === msg.id) {
          items[items.length - 1] = {
            kind: "text",
            id: last.id,
            content: textContent,
          };
        } else {
          items.push({ kind: "text", id: msg.id ?? "", content: textContent });
        }
      }

      const toolCalls = (msg as AIMessage).tool_calls;
      if (toolCalls?.length) {
        for (const tc of toolCalls) {
          const toolCallId = tc.id ?? tc.name ?? "";
          const resultExists = items.some(
            (c) => isToolCallResultItem(c) && c.toolCallId === toolCallId
          );
          if (!resultExists) {
            items.push({
              kind: "tool_call_start",
              id: msg.id ?? "",
              name: tc.name,
              toolCallId,
              input: tc.args,
            });
          }
        }
      }
    }

    if (msg.type === "tool") {
      const toolMsg = msg as ToolMessage;
      const body = messageContentToString(toolMsg.content);
      const toolCallId = toolMsg.tool_call_id ?? "";
      const toolName = toolMsg.name ?? "tool";
      const isError = toolMsg.status === "error" || isToolErrorBody(body);

      const matchingIndex = items.findIndex(
        (c): c is ToolCallStartItem =>
          c.kind === "tool_call_start" && c.toolCallId === toolCallId
      );

      if (matchingIndex !== -1) {
        const existing = items[matchingIndex] as ToolCallStartItem;
        items[matchingIndex] = {
          kind: "tool_call_result",
          id: existing.id,
          name: toolName,
          body,
          toolCallId,
          input: existing.input,
          isError,
        };
      } else {
        items.push({
          kind: "tool_call_result",
          id: toolMsg.id ?? "",
          name: toolName,
          body,
          toolCallId,
          isError,
        });
      }
    }
  }

  return items;
}

export function displayContentToText(items: DisplayContentItem[]): string {
  return items
    .filter(isTextItem)
    .map((i) => i.content)
    .join("");
}

export interface SubagentSource {
  toolCall: { args: { subagent_type?: string; description?: string } };
  messages: Message[];
}

const isDisplayableMessage = (m: Message) =>
  m.type === "ai" || (m.type as string) === "AIMessageChunk" || m.type === "tool";

/**
 * Drops the subagent's own `task` result. useStream files the parent's ToolMessage
 * for the `task` call (the subagent's final answer, already shown as the task
 * result) under the subagent too, where it would render as a nested, unnamed
 * subagent.
 */
const withoutOwnResult = (items: DisplayContentItem[], toolCallId: string) =>
  items.filter((item) => item.kind === "text" || item.toolCallId !== toolCallId);

export function subagentSourceToDisplayInfo(
  source: SubagentSource,
  toolCallId: string
): SubagentDisplayInfo {
  return {
    subagentType: source.toolCall.args.subagent_type,
    description: source.toolCall.args.description,
    items: withoutOwnResult(
      messagesToDisplayContent(source.messages.filter(isDisplayableMessage)),
      toolCallId
    ),
  };
}

/** What a running subagent is doing now, from its latest transcript item. */
export function subagentActivity(items: DisplayContentItem[]): string {
  const last = items.at(-1);
  if (!last) return "Starting";
  if (isTextItem(last)) return "Writing";
  if (isToolCallStartItem(last)) return `Calling ${last.name || "a tool"}`;
  return "Thinking";
}

export function attachSubagentDisplay(
  items: DisplayContentItem[],
  subagents: ReadonlyMap<string, SubagentSource>,
  cached?: Readonly<Record<string, SubagentDisplayInfo>>
): DisplayContentItem[] {
  return items.map((item) => {
    if (item.kind === "text" || item.name !== TASK_TOOL_NAME) return item;

    const source = subagents.get(item.toolCallId);
    const live = source
      ? subagentSourceToDisplayInfo(source, item.toolCallId)
      : undefined;
    const snapshot = cached?.[item.toolCallId];
    // Filtered here too: snapshots cached before the filter existed hold the result.
    const snapshotItems = snapshot
      ? withoutOwnResult(snapshot.items, item.toolCallId)
      : [];
    const input = item.input as
      | { subagent_type?: string; description?: string }
      | undefined;

    const subagent: SubagentDisplayInfo = {
      subagentType:
        live?.subagentType ?? snapshot?.subagentType ?? input?.subagent_type,
      description: live?.description ?? snapshot?.description ?? input?.description,
      items: live?.items.length ? live.items : snapshotItems,
    };

    return { ...item, subagent };
  });
}
